import React, { useState, useMemo } from 'react';
import { TableColumn, SimplePaginationInfo } from '../../types';
import Pagination from './Pagination';
import CellContent from './CellContent';
import EditIcon from '../../common/assets/icons/Edit.svg';

interface DataTableProps {
  columns: TableColumn[];
  data: any[];
  loading?: boolean;
  pagination?: SimplePaginationInfo;
  onPageChange?: (page: number) => void;
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearch?: (query: string) => void;
  actions?: React.ReactNode;
  onRowClick?: (row: any) => void;
  totalCountOverride?: number;
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  loading = false,
  pagination,
  onPageChange,
  onSort,
  searchable = false,
  searchPlaceholder = 'Search...',
  onSearch,
  actions,
  onRowClick,
  totalCountOverride,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Debug: Log data changes
      React.useEffect(() => {
      }, [data, columns]);

  const handleSort = (column: string) => {
    // Ignore sorting for spacer columns
    if (column === 'spacer') return;
    if (!onSort) return;
    
    let direction: 'asc' | 'desc' = 'asc';
    if (sortColumn === column && sortDirection === 'asc') {
      direction = 'desc';
    }
    
    setSortColumn(column);
    setSortDirection(direction);
    onSort(column, direction);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (onSearch) {
      onSearch(query);
    }
  };


      const filteredData = useMemo(() => {
    // Handle empty or invalid data
    if (!data || data.length === 0) {
      return [];
    }
    
    if (!searchQuery) return data;
    
    const filtered = data.filter((row) =>
      columns.some((column) => {
        if (column.key === 'spacer' || column.isEditColumn) return false; // exclude spacer and edit columns from search
        const value = row[column.key];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(searchQuery.toLowerCase());
      })
    );
    return filtered;
  }, [data, searchQuery, columns]);

      const paginatedData = useMemo(() => {
    if (!pagination) return filteredData;
    
    // Handle empty or invalid data
    if (!filteredData || filteredData.length === 0) {
      return [];
    }
    
    const startIndex = (pagination.page - 1) * pagination.limit;
    const endIndex = startIndex + pagination.limit;
    const paginated = filteredData.slice(startIndex, endIndex);
    return paginated;
  }, [filteredData, pagination]);

  // Check if any column has width specified to determine table layout
  const hasColumnWidths = useMemo(() => {
    return columns.some(column => column.width);
  }, [columns]);


  return (
    <div className="fade-in h-100 d-flex flex-column justify-content-between">
      {/* Table Header with Search and Actions */}
      {(searchable || actions) && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            {searchable && (
              <div className="input-group" style={{ width: '300px' }}>
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span> 
                <input
                  type="text"
                  className="form-control"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearch(e.target.value)}
                />
              </div>
            )}
          </div>
          {actions && <div>{actions}</div>}
        </div>
      )}  

      {/* Table */}
      <div className="table-responsive" style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <table 
          className="table table-hover mb-0" 
          style={{ 
            minWidth: '100%', 
            tableLayout: hasColumnWidths ? 'fixed' : 'auto',
            width: '100%'
          }}
        >
          <thead>
            <tr>
              {columns.map((column) => {
                // Special spacer column: fixed 10px transparent, no border, no sorting
                if (column.key === 'spacer') {
                  return (
                    <th
                      key="spacer"
                      style={{
                        width: '10px',
                        minWidth: '10px',
                        maxWidth: '10px',
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                      }}
                    >
                      {/* spacer */}
                    </th>
                  );
                }
                const columnStyle: React.CSSProperties = {
                  cursor: column.sortable ? 'pointer' : 'default',
                  userSelect: 'none',
                  backgroundColor: '#6E21A1',
                  borderBottom: 'none',
                  color: '#ffcc00',
                };

                // Apply width constraints only when specified for this specific column
                if (column.width) {
                  columnStyle.width = typeof column.width === 'number' ? `${column.width}px` : column.width;
                } else {
                  // For auto-width columns (no width specified), ensure they can grow
                  columnStyle.width = '100px';
                  columnStyle.minWidth = 'fit-content';
                }

                return (
                  <th
                    key={column.key}
                    className={column.sortable ? 'cursor-pointer text-secondary' : 'text-secondary'}
                    onClick={column.sortable ? () => handleSort(column.key) : undefined}
                    style={columnStyle}
                  >
                    <div className="d-flex align-items-center fw-normal">
                      {column.label}
                      {column.sortable && !column.isEditColumn && (
                        <i 
                          className={`fas fa-sort ms-2 text-secondary ${
                            sortColumn === column.key 
                              ? `fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}` 
                              : ''
                          }`}
                        ></i>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-5" style={{background: '#e9e6ec'}}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="mt-2">Loading...</div>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-5 text-muted">
                  <i className="fas fa-inbox fa-2x mb-3"></i>
                  <div>No data available</div>
                </td>
              </tr>
            ) : (
              paginatedData.map((row, index) => (
                <tr 
                  key={row.id || row.tblid || index}
                  onClick={() => onRowClick?.(row)}
                  style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {columns.map((column) => {
                    if (column.key === 'spacer') {
                      return (
                        <td
                          key={`spacer-${index}`}
                          style={{
                            width: '10px',
                            minWidth: '10px',
                            maxWidth: '10px',
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                          }}
                        />
                      );
                    }
                    const cellStyle: React.CSSProperties = {
                      color: '#6E21A1',
                      background: '#e9e6ec',
                      borderBottom: '1px solid #d6c9e1',
                      maxWidth: 0, // Important for ellipsis to work in table cells
                    };

                    // Apply width constraints to cells only when specified for this specific column
                    if (column.width) {
                      cellStyle.width = typeof column.width === 'number' ? `${column.width}px` : column.width;
                      cellStyle.maxWidth = cellStyle.width; // Ensure maxWidth matches width for ellipsis
                    } else {
                      // For auto-width columns (no width specified), ensure they can grow
                      cellStyle.width = 'auto';
                      cellStyle.minWidth = 'fit-content';
                      cellStyle.maxWidth = 'none';
                    }

                    // Render cell content
                    const renderCellContent = () => {
                      // Handle edit columns
                      if (column.isEditColumn && column.onEditClick) {
                        return (
                          <div 
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click when clicking edit icon
                              column.onEditClick!(row);
                            }}
                            style={{ cursor: 'pointer', display: 'inline-block' }}
                          >
                            {column.editIcon || <EditIcon width={16} height={16} />}
                          </div>
                        );
                      }

                      // Handle custom render functions
                      if (column.render) {
                        const renderedContent = column.render(
                          row[column.key],
                          row,
                          ((pagination?.page || 1) - 1) * (pagination?.limit || 0) + index,
                          pagination
                        );
                        
                        // If rendered content is a string, use CellContent for ellipsis/tooltip
                        if (typeof renderedContent === 'string') {
                          return <CellContent content={renderedContent} />;
                        }
                        
                        // For non-string rendered content, return as-is
                        return renderedContent;
                      }

                      // Handle regular content with ellipsis/tooltip
                      return <CellContent content={row[column.key]} />;
                    };

                    return (
                      <td key={column.key} style={cellStyle}>
                        {renderCellContent()}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <Pagination
          pagination={pagination}
          totalItems={totalCountOverride ?? filteredData.length}
          onPageChange={onPageChange || (() => {})}
        />
      )}

      {/* Results Summary */}
      {/* {pagination && (
        <div className="text-muted text-center mt-3">
          Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
          {Math.min(pagination.page * pagination.limit, filteredData.length)} of{' '}
          {filteredData.length} results
        </div>
      )} */}
    </div>
  );
};

export default DataTable;
